const CONTEST_NO_PURCHASE_DISCLOSURE = "NO PURCHASE, PAYMENT, OR DONATION OF ANY KIND IS NECESSARY TO ENTER OR WIN. A PURCHASE, PAYMENT, OR DONATION WILL NOT IMPROVE YOUR CHANCES OF WINNING. VOID WHERE PROHIBITED BY LAW.";
export const canadaContestRulesTemplate = {
    fallbackValues: {
        sponsorLegalName: "the organization running this Contest",
        entryOpenDatetime: "the date this Contest is published",
        prizesTable: "The prize(s) offered in this Contest are described on the campaign page.",
        winnersListRequestDeadline: "thirty (30) days after the end of the Contest Period",
        sweepstakesName: "this Contest",
    },
    build: ({ text, paragraph, fillValues, zeffyPrivacyPolicyUrl }) => ({
        title: fillValues("{sponsorLegalName} OFFICIAL CONTEST RULES").toUpperCase(),
        disclosure: CONTEST_NO_PURCHASE_DISCLOSURE,
        introduction: [
            {
                segments: [
                    { type: "strong", text: "How these rules work." },
                    text(' These are the Official Rules for this Contest (the "Contest"), run by {sponsorLegalName} on the Zeffy platform. All details specific to this Contest, including the Sponsor\'s identity, the entry levels, the dates, the prize(s) and their values, are stated directly in these rules below. Your entry in this Contest means these Official Rules form a binding agreement between you and the Sponsor, so please read them carefully before participating.'),
                ],
            },
        ],
        sections: [
            {
                id: "sponsor-and-platform-roles",
                title: "1. Sponsor and Platform Roles",
                paragraphs: [
                    paragraph('The sponsor of this Contest is {sponsorLegalName}, {sponsorEntityType}[[, {sponsorTaxIdLabel} {sponsorTaxId}]][[, with its address at {sponsorFullAddress}]] (the "Sponsor"). The Sponsor is solely responsible for the Contest, including the offering, funding, awarding and delivery of all prizes.'),
                    paragraph('Zeffy (operated by Simplyk, Inc.) provides the technology platform on which the Contest is hosted. Zeffy is not the sponsor, operator or administrator of the Contest, does not offer or award any prize, and is not responsible for the conduct of the Contest. Zeffy is a Released Party under Section 11. By obtaining an entry, you ("Entrant", "you") agree to be bound by these Official Rules and by the decisions of the Sponsor, which are final and binding on all matters relating to the Contest.'),
                ],
            },
            {
                id: "eligibility",
                title: "2. Eligibility",
                paragraphs: [
                    paragraph("The Contest is open to natural persons who, at the time of entry, are legal residents of Canada, and who have reached the age of majority in their province or territory of residence (18 or 19 depending on the province/territory)."),
                    paragraph("The Contest is void where prohibited or restricted by law. You are not eligible to participate if you appear on the Canadian sanctions lists maintained by the Government of Canada under the Special Economic Measures Act (SEMA) or related legislation."),
                    paragraph("Employees, officers, directors, agents and representatives of the Sponsor, and of any party involved in the creation, administration or fulfillment of the Contest, together with the members of their immediate families and persons living in the same household, are not eligible to enter."),
                ],
            },
            {
                id: "contest-period",
                title: "3. Contest Period",
                paragraphs: [
                    paragraph('The Contest begins on {entryOpenDatetime} and ends on {entryCloseDatetime} ({timezoneLabel}) (the "Contest Period"). Entries submitted outside the Contest Period are not eligible. The Sponsor\'s timekeeping systems are the official time for the Contest.'),
                ],
            },
            {
                id: "how-to-enter",
                title: "4. How to Enter",
                paragraphs: [
                    paragraph("There are multiple ways to enter, and no method of entry gives a better chance of winning than any other."),
                    {
                        segments: [
                            { type: "strong", text: "(a) Enter by making a donation online." },
                            text(" During the Contest Period, visit {campaignUrl}, select your donation level, complete the required contact fields, and submit your payment. Entries are issued at the following levels:"),
                        ],
                    },
                    paragraph("{entryLevelsTable}"),
                    paragraph("The maximum number of entries per person by donation is {maxPaidEntriesPerPerson}."),
                    {
                        segments: [
                            { type: "strong", text: "(b) Enter for free (No Purchase Necessary)." },
                            text(" During the Contest Period, you may obtain an entry without making any donation or payment. Follow these steps:\n1. Open the free entry form "),
                            { type: "freeEntryLink", text: "here" },
                            text("\n2. Complete the required contact fields.\n3. Once your request is received, you will receive confirmation of your entry number(s) by email."),
                        ],
                    },
                    paragraph("Each free entry request grants {freeEntriesPerRequest} entry/entries. The limit is {freeEntryLimit}."),
                    paragraph("Entrants must provide truthful and accurate information. Providing false information, attempting to obtain more than the permitted number of entries, or using automated entries will void the affected entries and result in disqualification."),
                ],
            },
            {
                id: "not-tax-deductible",
                title: "5. Amounts Paid Are Not Tax-Deductible",
                paragraphs: [
                    {
                        segments: [
                            text('An entrant who donates to enter receives the chance to win a prize in return. Under Canada Revenue Agency (CRA) rules, payments or donations made to obtain entries in a contest or draw confer an "advantage" and '),
                            { type: "strong", text: "are not deductible as charitable contributions" },
                            text(". No charitable tax receipt will be issued for these amounts. In Canada, the value of any prize won in a lottery or contest is generally not taxable as income to the winner."),
                        ],
                    },
                ],
            },
            {
                id: "odds-and-selection",
                title: "6. Odds and Selection of Winner",
                paragraphs: [
                    {
                        segments: [
                            { type: "strong", text: "Odds of winning:" },
                            text(" The odds of winning depend on the total number of eligible entries received. {oddsStatement}"),
                        ],
                    },
                    {
                        segments: [
                            { type: "strong", text: "Drawing:" },
                            text(" On or about {drawingDatetime} ({timezoneLabel}), the Sponsor or its designee will select the potential winner(s) in a random drawing from among all eligible entries, using {drawingMethod}.[[ The drawing will take place at {sponsorFullAddress}.]]"),
                        ],
                    },
                    paragraph("Each entry is eligible for every prize until that entry is drawn. Once an entry is drawn it is removed from the pool, so no single entry can win more than one prize. {onePrizePerPersonStatement}"),
                ],
            },
            {
                id: "prizes",
                title: "7. Prizes",
                paragraphs: [
                    paragraph("{numberOfPrizes} prize(s) will be awarded, as follows:"),
                    paragraph("{prizesTable}"),
                    paragraph("Each Prize is non-transferable and may not be substituted or redeemed for cash by the winner, except that the Sponsor reserves the right to substitute a prize of equal or greater value if an advertised Prize becomes unavailable."),
                ],
            },
            {
                id: "winner-notification",
                title: "8. Winner Notification and Mandatory Skill-Testing Question",
                paragraphs: [
                    {
                        segments: [
                            { type: "strong", text: "Skill-Testing Question:" },
                            text(" Under Canadian law, to be declared a winner and claim a prize, the selected entrant must first correctly answer, unaided and within a set timeframe, a mathematical skill-testing question administered by the Sponsor via phone, email, or digital form."),
                        ],
                    },
                    {
                        segments: [
                            { type: "strong", text: "Notification:" },
                            text(" Each potential winner will be notified by {winnerNotificationMethod}, on or about {winnerNotificationDate}. The potential winner must respond within {winnerResponseDeadlineDays} day(s), correctly answer the skill-testing question, and complete any required declaration and release of liability within {prizeAcceptanceDeadlineDays} day(s)."),
                        ],
                    },
                    paragraph("If a potential winner cannot be reached, fails the skill-testing question, fails to return required documentation, or declines the Prize, that Prize will be forfeited and an alternate winner may be selected."),
                    paragraph("Prize delivery: {prizeDeliveryTerms}"),
                ],
            },
            {
                id: "publicity",
                title: "9. Publicity",
                paragraphs: [
                    paragraph("Subject to the winner's consent and applicable privacy laws, acceptance of a Prize constitutes permission for the Sponsor to use the winner's name, city and province of residence, and likeness for advertising and publicity purposes in any media, without further compensation."),
                ],
            },
            {
                id: "privacy",
                title: "10. Privacy",
                paragraphs: [
                    {
                        segments: [
                            text("Personal information collected in connection with the Contest is used to administer the Contest, contact entrants, deliver prizes, and comply with legal requirements. Data will be handled in accordance with the Personal Information Protection and Electronic Documents Act (PIPEDA) and applicable provincial privacy laws. For more information: {sponsorPrivacyPolicyReference} and "),
                            { type: "externalLink", text: "Zeffy’s Privacy Policy", href: zeffyPrivacyPolicyUrl },
                            text("."),
                        ],
                    },
                ],
            },
            {
                id: "release-and-liability",
                title: "11. Release and Limitation of Liability",
                paragraphs: [
                    paragraph('"Released Parties" means the Sponsor and Zeffy, together with their respective parents, affiliates, officers, directors, employees, and agents.'),
                    paragraph("To the fullest extent permitted by applicable Canadian law, by entering, you release the Released Parties from any liability for any loss, harm, damage, injury, cost or expense arising out of your participation in the Contest or your acceptance or use of any Prize. The Released Parties are not responsible for technical failures, network outages, or entries that are late, lost, or misdirected."),
                ],
            },
            {
                id: "general-conditions",
                title: "12. General Conditions",
                paragraphs: [
                    paragraph("The Sponsor reserves the right to cancel, suspend or modify the Contest if fraud, technical failure, or any other cause beyond its reasonable control affects the integrity of the Contest, subject to any applicable legal requirements."),
                ],
            },
            {
                id: "governing-law",
                title: "13. Disputes and Governing Law",
                paragraphs: [
                    paragraph("These Official Rules and the rights and obligations of the entrants and the Sponsor shall be governed by and construed in accordance with the laws of the province or territory in which the entrant resides, and the federal laws of Canada applicable therein. Any dispute arising out of or relating to the Contest will be resolved in the competent courts of the entrant's province or territory of residence."),
                    paragraph("(Note: The former requirements to submit disputes to the Régie des alcools, des courses et des jeux (RACJ) in Quebec have been abolished as of October 27, 2023, and are no longer applicable)."),
                ],
            },
            {
                id: "winners-list",
                title: "14. Winners List",
                paragraphs: [
                    paragraph('To request the name of the winner or winners, send a self-addressed stamped envelope to {sponsorLegalName}[[, {sponsorFullAddress}]], referencing "{sweepstakesName} - Winners List", to be received no later than {winnersListRequestDeadline}.'),
                ],
            },
            {
                id: "contact",
                title: "15. Contact",
                paragraphs: [
                    paragraph("For questions about this Contest, contact the Sponsor at {sponsorContactEmail}[[, or by phone at {sponsorContactPhone}]]."),
                ],
            },
        ],
    }),
};
